'use client'

import * as React from 'react'
import Image from 'next/image'
import { ImgWithFallback } from '@/components/ui/img-with-fallback'
import {
  ShoppingBag, Heart, MessageCircle, Volume2, Check,
  ChevronLeft, ChevronRight, X, ShoppingCart,
} from 'lucide-react'

// Interactive shoppable hero: a live mini version of the Idukki widget.
//   - tap a hotspot pin  → the product drawer slides up
//   - swipe / arrows / dots → next UGC video
//   - "Add to cart" morphs to "Added ✓" + bumps the cart badge
// Driven by real demo posts (image/video + hotspots → products), so the hero
// shows the actual product experience instead of a canned loop.

export type HeroSlideProduct = {
  x: number
  y: number
  name: string
  price: string
  msrp?: string
  badge?: string
  image: string
}
export type HeroSlide = {
  image: string
  videoSrc?: string
  user: string
  quote?: string
  products: HeroSlideProduct[]
}

export function InteractiveShoppableHero({
  slides,
  brandLabel = 'idukki · live preview',
}: {
  slides: HeroSlide[]
  brandLabel?: string
}) {
  const [index, setIndex] = React.useState(0)
  const [active, setActive] = React.useState<HeroSlideProduct | null>(null)
  const [added, setAdded] = React.useState(false)
  const [cart, setCart] = React.useState(0)
  const touchY = React.useRef<number | null>(null)
  const videoRef = React.useRef<HTMLVideoElement | null>(null)
  const [reduced, setReduced] = React.useState(false)

  const count = slides.length
  const slide = slides[index]

  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const set = () => setReduced(mq.matches)
    set()
    mq.addEventListener('change', set)
    return () => mq.removeEventListener('change', set)
  }, [])

  const goTo = React.useCallback(
    (next: number) => {
      if (count === 0) return
      setIndex(((next % count) + count) % count)
      setActive(null)
      setAdded(false)
    },
    [count],
  )

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') goTo(index - 1)
    else if (e.key === 'ArrowRight') goTo(index + 1)
    else if (e.key === 'Escape') setActive(null)
  }

  const onTouchStart = (e: React.TouchEvent) => {
    touchY.current = e.touches[0]?.clientY ?? null
  }
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchY.current == null) return
    const dy = (e.changedTouches[0]?.clientY ?? 0) - touchY.current
    // Reels-style vertical swipe: up → next, down → previous.
    if (Math.abs(dy) > 44) goTo(index + (dy < 0 ? 1 : -1))
    touchY.current = null
  }

  function addToCart() {
    setAdded(true)
    setCart((c) => c + 1)
  }

  // Pause the video while the shopper reads the product drawer; resume after.
  React.useEffect(() => {
    const v = videoRef.current
    if (!v) return
    if (active) v.pause()
    else v.play().catch(() => {})
  }, [active, index])

  // Auto-advance: image slides on a timer; videos advance when they end (see
  // onEnded). Never while the drawer is open or under prefers-reduced-motion.
  React.useEffect(() => {
    if (active || reduced || count <= 1 || slide?.videoSrc) return
    const t = setTimeout(() => goTo(index + 1), 6000)
    return () => clearTimeout(t)
  }, [active, reduced, count, slide?.videoSrc, index, goTo])

  if (!slide) return null

  return (
    <div className="relative mx-auto w-full max-w-[360px]">
      {/* Floating runtime badge, outside the phone for contrast */}
      <span className="absolute -left-2 top-6 z-20 inline-flex items-center gap-1 rounded-full border bg-card/95 px-2.5 py-1 text-[10px] font-medium text-foreground shadow-md">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-1.5 w-1.5 animate-ping rounded-full bg-emerald-500 opacity-70 motion-reduce:hidden" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
        </span>
        Live
      </span>

      {/* Prev / next — desktop arrows just outside the phone */}
      {count > 1 && (
        <>
          <button
            type="button"
            onClick={() => goTo(index - 1)}
            aria-label="Previous video"
            className="absolute -left-3 top-1/2 z-30 hidden -translate-y-1/2 items-center justify-center rounded-full border bg-card/90 p-2 text-foreground shadow-md transition hover:-translate-y-[calc(50%+2px)] hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:flex"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => goTo(index + 1)}
            aria-label="Next video"
            className="absolute -right-3 top-1/2 z-30 hidden -translate-y-1/2 items-center justify-center rounded-full border bg-card/90 p-2 text-foreground shadow-md transition hover:-translate-y-[calc(50%+2px)] hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:flex"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}

      {/* Phone shell */}
      <div
        role="group"
        aria-label="Idukki shoppable widget — tap a pin to shop, swipe for the next video"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        className="relative mx-auto aspect-[9/19] w-full select-none overflow-hidden rounded-[2.2rem] border-[10px] border-slate-900 bg-slate-900 shadow-[0_30px_60px_-20px_rgba(15,23,42,0.5),0_18px_36px_-18px_rgba(15,23,42,0.3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        {/* Notch */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-30 flex justify-center">
          <div className="h-5 w-24 rounded-b-2xl bg-slate-900" />
        </div>

        {/* Top status row */}
        <div className="absolute inset-x-0 top-1 z-20 flex items-center justify-between px-4 text-[9px] font-medium text-white/90">
          <span>9:41</span>
          <div className="relative inline-flex items-center gap-1">
            <ShoppingCart className="h-3 w-3" />
            {cart > 0 && (
              <span className="absolute -right-1.5 -top-1 inline-flex h-3 w-3 items-center justify-center rounded-full bg-emerald-500 text-[7px] font-bold text-white">
                {cart}
              </span>
            )}
          </div>
        </div>

        {/* Brand bar */}
        <div className="absolute inset-x-0 top-6 z-10 flex items-center justify-between bg-black/40 px-3 py-1.5 text-[9px] text-white backdrop-blur">
          <span className="font-medium tracking-wide">{brandLabel}</span>
          <Volume2 className="h-3 w-3" />
        </div>

        {/* Video / poster background (remounts per slide so it autoplays) */}
        {slide.videoSrc ? (
          <video
            key={slide.videoSrc}
            ref={videoRef}
            src={slide.videoSrc}
            autoPlay
            muted
            loop={reduced || count <= 1}
            playsInline
            preload="metadata"
            // Some demo slides reuse the video's own URL as `image` (no
            // distinct static thumbnail exists for them) — passing that to
            // `poster` doesn't render anything (browsers can't decode a
            // .webm as an image) but still makes the browser fetch the
            // entire video a second time just for the failed poster attempt.
            // Only pass a poster when it's a genuinely different asset.
            poster={slide.image !== slide.videoSrc ? slide.image : undefined}
            // The image-only branch below gets Next/Image's `priority` for
            // slide 0 (eager load + fetchpriority=high). Every hero slide
            // today actually has a videoSrc, so this branch — not that one —
            // is what the first paint depends on, and it had no priority hint
            // at all: the poster loaded at default priority while autoplay
            // simultaneously requested the video body, so slide 0 could paint
            // as a bare dark frame until one of the two won the race.
            // React 18.3+ maps fetchPriority to the fetchpriority DOM
            // attribute correctly at runtime regardless of element; the
            // @types/react VideoHTMLAttributes just hasn't caught up yet.
            // @ts-expect-error — fetchPriority is valid on <video> at runtime, not yet in VideoHTMLAttributes' types
            fetchPriority={index === 0 ? 'high' : 'auto'}
            onEnded={() => { if (!reduced && !active && count > 1) goTo(index + 1) }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <ImgWithFallback
            key={slide.image}
            src={slide.image}
            alt=""
            fill
            sizes="360px"
            className="object-cover"
            priority={index === 0}
          />
        )}

        {/* Username + engagement strip */}
        <div className="absolute inset-x-0 bottom-0 z-10 flex items-end justify-between bg-gradient-to-t from-black/75 via-black/30 to-transparent p-3 text-[10px] text-white">
          <div className="min-w-0 pr-2">
            <p className="truncate font-semibold">{slide.user}</p>
            {slide.quote && <p className="truncate opacity-80">{slide.quote}</p>}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="inline-flex items-center gap-1"><Heart className="h-3 w-3" /> 4.2k</span>
            <span className="inline-flex items-center gap-1"><MessageCircle className="h-3 w-3" /> 187</span>
          </div>
        </div>

        {/* Hotspot pins — tappable */}
        {slide.products.map((p, i) => (
          <button
            key={`${index}-${i}`}
            type="button"
            onClick={() => { setActive(p); setAdded(false) }}
            aria-label={`Shop ${p.name}`}
            className="group absolute z-20 -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${p.x}%`, top: `${p.y}%` }}
          >
            <span className="absolute left-1/2 top-1/2 h-9 w-9 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/60 motion-safe:animate-ping" />
            <span className="relative flex h-7 w-7 items-center justify-center rounded-full bg-white text-foreground shadow-lg ring-2 ring-white/70 transition group-hover:scale-110 group-focus-visible:scale-110">
              <ShoppingBag className="h-3.5 w-3.5 text-primary" />
            </span>
            {/* One "Shop the look" cue on the primary pin so the affordance reads. */}
            {i === 0 && !active && (
              <span className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand-navy px-2 py-0.5 text-[9px] font-semibold text-white shadow-md">
                Shop the look
              </span>
            )}
          </button>
        ))}

        {/* Product drawer — slides up when a pin is tapped */}
        <div
          className={`absolute inset-x-0 bottom-0 z-40 rounded-t-3xl border-t bg-card p-4 shadow-2xl transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${
            active ? 'translate-y-0' : 'pointer-events-none translate-y-full'
          }`}
        >
          <button
            type="button"
            onClick={() => setActive(null)}
            aria-label="Close"
            className="absolute right-3 top-3 rounded-full p-1 text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-muted-foreground/30" />
          {active && (
            <>
              <div className="flex items-center gap-3">
                <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-secondary">
                  <ImgWithFallback src={active.image} alt={active.name} fill sizes="56px" className="object-cover" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12px] font-semibold leading-tight">{active.name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    <span className="font-semibold text-foreground">{active.price}</span>
                    {active.msrp && <span className="ml-1 line-through opacity-60">{active.msrp}</span>}
                    {' · Free shipping'}
                  </p>
                  <div className="mt-1 flex gap-1">
                    <span className="h-3 w-3 rounded-full border bg-neutral-200" />
                    <span className="h-3 w-3 rounded-full border bg-amber-200" />
                    <span className="h-3 w-3 rounded-full border-2 border-foreground bg-neutral-700" />
                  </div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-[1fr_auto] gap-2">
                <button
                  type="button"
                  onClick={addToCart}
                  className={`flex items-center justify-center gap-1 rounded-full px-4 py-2 text-[12px] font-semibold text-white shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    added ? 'bg-emerald-500' : 'bg-brand-navy hover:brightness-110'
                  }`}
                >
                  {added ? (<><Check className="h-3.5 w-3.5" strokeWidth={3} /> Added</>) : 'Add to cart'}
                </button>
                <button
                  type="button"
                  className="rounded-full border px-3 py-2 text-[11px] font-medium text-foreground transition hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  aria-label="Save"
                >
                  <Heart className="h-3.5 w-3.5" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Dots — button is a 24px+ tap target (WCAG 2.5.8); the visible pill
          inside stays the original slim indicator. */}
      {count > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Go to video ${i + 1}`}
              aria-current={i === index}
              className="group flex h-6 w-6 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <span
                aria-hidden="true"
                className={`h-1.5 rounded-full transition-[width,background-color] ${
                  i === index ? 'w-6 bg-brand-navy' : 'w-1.5 bg-muted-foreground/30 group-hover:bg-muted-foreground/60'
                }`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
